/**
 * Read-Only Mode Service
 *
 * Manages the application's degraded read-only mode state.
 * This mode is activated when the app cannot establish proper
 * communication with the host environment (e.g., user context load fails).
 *
 * In read-only mode:
 * - Users can view data but cannot perform write operations
 * - An alert banner is displayed explaining the limitation
 * - ACL checks for write/create/delete operations return false
 */

import { Injectable, signal, computed } from '@angular/core';

export interface ReadOnlyState {
  isReadOnly: boolean;
  reason?: string;
  timestamp?: Date;
}

@Injectable({ providedIn: 'root' })
export class ReadOnlyModeService {
  private readonly state = signal<ReadOnlyState>({
    isReadOnly: false,
    reason: undefined,
    timestamp: undefined,
  });

  /**
   * Signal indicating if the app is in read-only mode
   */
  readonly isReadOnly = computed(() => this.state().isReadOnly);

  /**
   * Signal with the reason for read-only mode (if active)
   */
  readonly reason = computed(() => this.state().reason);

  /**
   * Signal with the full read-only state
   */
  readonly readOnlyState = computed(() => this.state());

  /**
   * Enable read-only mode with a reason message
   *
   * @param reason - Human-readable explanation of why read-only mode is active
   *
   * @example
   * ```typescript
   * readOnlyModeService.enableReadOnly('Unable to verify user permissions. Some features are temporarily unavailable.');
   * ```
   */
  enableReadOnly(reason: string): void {
    this.state.set({
      isReadOnly: true,
      reason,
      timestamp: new Date(),
    });

    console.warn('[ReadOnlyModeService] Read-only mode enabled:', reason);
  }

  /**
   * Disable read-only mode (restore full functionality)
   */
  disableReadOnly(): void {
    this.state.set({
      isReadOnly: false,
      reason: undefined,
      timestamp: undefined,
    });

    console.log('[ReadOnlyModeService] Read-only mode disabled');
  }

  /**
   * Check if a write operation should be allowed.
   * Returns false if in read-only mode.
   *
   * @returns true if write operations are allowed, false otherwise
   */
  canWrite(): boolean {
    return !this.state().isReadOnly;
  }

  /**
   * Get the current read-only state as a snapshot
   */
  getState(): ReadOnlyState {
    return { ...this.state() };
  }
}
